import { container } from './di/container';
import { ICollidable, Bounds } from './collision/types';
import { SoundConfig, SoundEventType } from './audio/types';
import { SoundManager } from './audio/sound-manager';
import { DEBUG } from './debug';

export class Animal implements ICollidable {
	x: number;
	y: number;
	maxHealth: number;
	currentHealth: number;
	readonly collisionType: string = 'animal';

	// Sprite dimensions (can be overridden by subclasses)
	spriteWidth: number = 32;
	spriteHeight: number = 32;
	protected spriteHorizontalOffset: number = 0;

	// Collision box configuration (can be overridden by subclasses)
	collisionWidth: number = 24;
	collisionHeight: number = 24;
	collisionOffsetX: number = 4; // (spriteWidth - collisionWidth) / 2
	collisionOffsetY: number = 4; // (spriteHeight - collisionHeight) / 2

	// Sound configuration (can be overridden by subclasses)
	protected sounds: SoundConfig = {};

	// Callbacks for health changes
	onDamage?: (currentHealth: number) => void;
	onHeal?: (currentHealth: number) => void;
	onDeath?: () => void;

	constructor(x: number, y: number, maxHealth: number = 9) {
		this.x = x;
		this.y = y;
		this.maxHealth = maxHealth;
		this.currentHealth = maxHealth;
	}

	protected playSound(type: SoundEventType): void {
		const soundId = this.sounds[type];
		if (soundId) {
			const soundManager = container.resolve(SoundManager);
			soundManager.play(soundId);
		}
	}

	/**
	 * Draw debug collision boxes if DEBUG.COLLISION is enabled
	 * Green: sprite bounds, Blue: collision box, Fuschia: center of gravity
	 */
	protected drawDebugCollision(): void {
		if (!DEBUG.COLLISION) return;

		// Sprite boyutu (yeşil)
		noFill();
		stroke(0, 255, 0);
		strokeWeight(2);
		rect(this.x, this.y, this.spriteWidth, this.spriteHeight);

		// Collision box (mavi)
		stroke(0, 150, 255);
		strokeWeight(1);
		rect(
			this.x + this.collisionOffsetX,
			this.y + this.collisionOffsetY,
			this.collisionWidth,
			this.collisionHeight
		);

		// Collision box'ın tam ortası ama en altında bir nokta (fuschia - ağırlık merkezi)
		const collisionCenterX = this.x + this.collisionOffsetX + this.collisionWidth / 2;
		const collisionBottomY = this.y + this.collisionOffsetY + this.collisionHeight;
		noStroke();
		fill(255, 0, 255); // Fuschia
		ellipse(collisionCenterX, collisionBottomY, 4, 4);
	}

	takeDamage(amount: number = 1): void {
		this.currentHealth = Math.max(0, this.currentHealth - amount);
		this.onDamage?.(this.currentHealth);
		if (this.currentHealth <= 0) {
			this.onDeath?.();
		}
	}

	heal(amount: number = 1): void {
		this.currentHealth = Math.min(this.maxHealth, this.currentHealth + amount);
		this.onHeal?.(this.currentHealth);
	}

	isDead(): boolean {
		return this.currentHealth <= 0;
	}

	resetHealth(): void {
		this.currentHealth = this.maxHealth;
	}

	// ICollidable implementation
	getBounds(): Bounds {
		return {
			x: this.x + this.collisionOffsetX,
			y: this.y + this.collisionOffsetY,
			width: this.collisionWidth,
			height: this.collisionHeight,
		};
	}

	isActive(): boolean {
		return !this.isDead();
	}

	onCollision(_other: ICollidable): void {
		// Base implementation does nothing
		// Subclasses can override to handle specific collision types
	}
}
